import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiAlertOutline } from '@mdi/js'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '../ui/sheet'
import { Button } from '../ui/button'
import { useAssetPropertyHistory, usePropertyDefinition, refreshAssetProperties, refreshIssues } from '../../hooks/useQueries'
import { PropertyHistoryTimeline } from './PropertyHistoryTimeline'
import { getLocalizedName } from '../../lib/localization'
import { EnumPropertyDisplay, getBooleanDisplay } from './PropertyInputUtils'
import { apiClient } from '../../api/client'
import { SECTION_ICONS } from '../../lib/section-icons'

/** Pending `to_approve` candidate for the property (a value conflict). */
interface ConflictCandidate {
    uid?: string
    value?: any
    flags?: string[] | null
    source_details?: any
}

interface PropertyHistoryDrawerProps {
    org: string
    assetUid: string
    propertyUid: string | undefined
    onClose: () => void
    /**
     * Pending `to_approve` candidates for this property. When present, a
     * conflict-resolution block is rendered above the timeline so the user can
     * adopt or dismiss each proposed value right where the versions are shown.
     */
    candidates?: ConflictCandidate[]
}

export function PropertyHistoryDrawer({ org, assetUid, propertyUid, onClose, candidates = [] }: PropertyHistoryDrawerProps) {
    const { t, i18n } = useTranslation('assets')
    const { t: tData } = useTranslation('data')
    const { t: tCommon } = useTranslation('common')
    const queryClient = useQueryClient()
    const [showRetracted, setShowRetracted] = useState(false)
    const [processing, setProcessing] = useState(false)
    const isOpen = Boolean(propertyUid)

    const { data: entries = [], isLoading } = useAssetPropertyHistory(org, assetUid, propertyUid)
    const { data: propertyDefinition } = usePropertyDefinition(org, propertyUid || '')

    const propertyName = propertyDefinition
        ? getLocalizedName(propertyDefinition.name, i18n.language) || propertyDefinition.code || propertyUid
        : t('detail.property_history')

    const hasConflict = candidates.length > 0

    const formatValue = useCallback((value: any) => {
        if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>
        const type = propertyDefinition?.data_type
        if (type === 'enum' && propertyDefinition?.uid) {
            return <EnumPropertyDisplay propertyDefinitionUid={propertyDefinition.uid} value={value as string | string[]} org={org} />
        }
        if (type === 'boolean') {
            const b = typeof value === 'boolean' ? value : value === 'true'
            return <>{getBooleanDisplay(b, propertyDefinition?.properties?.semantic as string | undefined, tCommon)}</>
        }
        if (type === 'date' && typeof value === 'string') {
            const d = new Date(value)
            if (!isNaN(d.getTime())) return <>{d.toLocaleDateString()}</>
        }
        if (Array.isArray(value)) return <>{value.join(', ')}</>
        if (typeof value === 'object') return <>{JSON.stringify(value)}</>
        return <>{String(value)}</>
    }, [propertyDefinition, org, tCommon])

    const resolve = useCallback(async (approve: string[], reject: string[]) => {
        setProcessing(true)
        try {
            await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/approve', {
                params: { path: { orgCode: org, assetUid } },
                body: { approve, reject },
            })
            refreshAssetProperties(queryClient, org, assetUid)
            refreshIssues(queryClient, org)
        } finally {
            setProcessing(false)
        }
    }, [org, assetUid, queryClient])

    const candidateUids = candidates.map((c) => c.uid).filter((u): u is string => Boolean(u))

    return (
        <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
            <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col" data-testid="property-history-drawer">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        {hasConflict && <AppIcon path={mdiAlertOutline} size="md" className="text-warning" />}
                        {propertyName}
                    </SheetTitle>
                    <SheetDescription>{hasConflict ? tData('asset.conflict_desc') : t('detail.property_history')}</SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
                    {/* Conflict resolution — proposed values awaiting validation */}
                    {hasConflict && (
                        <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/5 p-3" data-testid="conflict-block">
                            <div className="text-xs font-semibold uppercase tracking-wide text-warning">
                                {tData('asset.conflict_incoming', { count: candidates.length })}
                            </div>
                            {candidates.map((cand, i) => {
                                const filename = cand.source_details?.document_filename as string | undefined
                                const docUid = cand.source_details?.document_uid as string | undefined
                                const page = cand.source_details?.page as number | undefined
                                const confidence = cand.source_details?.confidence as number | undefined
                                return (
                                    <div key={cand.uid || i} className="flex flex-wrap items-center gap-2 border-t border-warning/20 pt-2 first:border-t-0 first:pt-0" data-testid={`conflict-candidate-${cand.uid || i}`}>
                                        <span className="text-sm font-medium text-foreground">{formatValue(cand.value)}</span>
                                        {docUid && filename && (
                                            <Link
                                                to="/$org/documents/$docUid"
                                                params={{ org, docUid }}
                                                search={page != null ? { page } : undefined}
                                                className="inline-flex max-w-[220px] items-center gap-1 truncate text-xs text-muted-foreground hover:text-foreground hover:underline"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <AppIcon path={SECTION_ICONS.documents} size="xs" className="flex-shrink-0" />
                                                <span className="truncate">{filename}</span>
                                                {page != null && <span className="flex-shrink-0">· p.{page}</span>}
                                            </Link>
                                        )}
                                        {confidence != null && <span className="text-[11px] text-muted-foreground">{Math.round(confidence * 100)}%</span>}
                                        <div className="ml-auto flex gap-1">
                                            <Button
                                                size="sm"
                                                className="h-7"
                                                onClick={() => cand.uid && resolve([cand.uid], candidateUids.filter((u) => u !== cand.uid))}
                                                disabled={processing || !cand.uid}
                                                data-testid={`conflict-adopt-${cand.uid || i}`}
                                            >
                                                <AppIcon path={mdiCheck} size="xs" className="mr-1" />
                                                {tData('asset.conflict_adopt')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7"
                                                onClick={() => cand.uid && resolve([], [cand.uid])}
                                                disabled={processing || !cand.uid}
                                                data-testid={`conflict-dismiss-${cand.uid || i}`}
                                            >
                                                <AppIcon path={mdiClose} size="xs" className="mr-1" />
                                                {tData('asset.conflict_dismiss')}
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="text-sm text-muted-foreground py-4">{t('detail.loading_properties')}</div>
                    ) : (
                        <PropertyHistoryTimeline
                            org={org}
                            entries={entries}
                            propertyDefinition={propertyDefinition}
                            showRetracted={showRetracted}
                        />
                    )}
                </div>

                <SheetFooter className="flex flex-row items-center justify-between gap-2 border-t pt-3">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showRetracted}
                            onChange={e => setShowRetracted(e.target.checked)}
                            data-testid="toggle-show-retracted"
                        />
                        {t('detail.history_show_retracted')}
                    </label>
                    <SheetClose asChild>
                        <Button variant="outline" size="sm" data-testid="close-property-history">
                            {t('detail.close')}
                        </Button>
                    </SheetClose>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
